import { useState } from "react";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Search, Filter, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SearchResult {
  id: string;
  title: string;
  plainContent: string;
  tags: string[];
  mood?: string;
  createdAt: string;
  snippet: string;
  matchCount: number;
}

interface PaginatedSearchResponse {
  results: SearchResult[];
  total: number;
  hasMore: boolean;
}

export default function JournalSearch() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const limit = 50;
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    mood: "",
    tags: "",
    category: "",
    pinnedOnly: false
  });

  // Fetch categories
  const { data: categories = [] } = useQuery<{ id: string; name: string; color: string }[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });
  
  // Build query params function
  const buildParams = (pageParam = 0) => {
    const params = new URLSearchParams();
    if (searchQuery) params.append("query", searchQuery);
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.mood) params.append("mood", filters.mood);
    if (filters.tags) params.append("tags", filters.tags);
    if (filters.category) params.append("category", filters.category);
    if (filters.pinnedOnly) params.append("pinnedOnly", "true");
    params.append("limit", limit.toString());
    params.append("offset", pageParam.toString());
    return params;
  };

  // Use useInfiniteQuery
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ["/api/journal/search", searchQuery, filters],
    queryFn: async ({ pageParam = 0 }) => {
      const params = buildParams(pageParam);
      const response = await fetch(`/api/journal/search?${params}`);
      return response.json();
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        return allPages.reduce((acc, page) => acc + page.results.length, 0);
      }
      return undefined;
    },
    initialPageParam: 0,
    enabled: true
  });

  // Flatten results from all pages
  const results = data?.pages.flatMap(page => page.results) || [];
  const total = data?.pages[0]?.total || 0;
  
  const escapeRegex = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };
  
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const escapedQuery = escapeRegex(query);
    const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-800">{part}</mark>
        : part
    );
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };
  
  return (
    <div className="w-full p-6 space-y-6">
      <h1 className="text-3xl font-bold">Search Journal</h1>
      
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search titles, content, and tags..."
            className="pl-10"
            data-testid="input-search"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-2 top-2"
              onClick={() => handleSearchChange("")}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          data-testid="button-toggle-filters"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </div>
      
      {showFilters && (
        <Card className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange({ ...filters, startDate: e.target.value })}
                data-testid="input-start-date"
              />
            </div>
            <div>
              <label className="text-sm font-medium">End Date</label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange({ ...filters, endDate: e.target.value })}
                data-testid="input-end-date"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Category</label>
              <Select 
                value={filters.category || "all"} 
                onValueChange={(value) => handleFilterChange({ ...filters, category: value === "all" ? "" : value })}
              >
                <SelectTrigger data-testid="select-search-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                  <CategorySelectGroups categories={categories} />
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Mood</label>
              <Input
                value={filters.mood}
                onChange={(e) => handleFilterChange({ ...filters, mood: e.target.value })}
                placeholder="e.g., happy, sad"
                data-testid="input-mood-filter"
              />
            </div>
            <div></div>
          </div>
          <div>
            <label className="text-sm font-medium">Tags (comma-separated)</label>
            <Input
              value={filters.tags}
              onChange={(e) => handleFilterChange({ ...filters, tags: e.target.value })}
              placeholder="e.g., work, personal"
              data-testid="input-tags-filter"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={filters.pinnedOnly}
              onChange={(e) => handleFilterChange({ ...filters, pinnedOnly: e.target.checked })}
              data-testid="checkbox-pinned-only"
            />
            <label>Pinned entries only</label>
          </div>
        </Card>
      )}
      
      <div className="space-y-4">
        {isLoading && <p>Searching...</p>}
        
        {!isLoading && results.length === 0 && (searchQuery || Object.values(filters).some(Boolean)) && (
          <p className="text-muted-foreground">No results found</p>
        )}

        {total > 0 && !isLoading && (
          <div className="text-sm text-muted-foreground" data-testid="text-result-count">
            Showing {results.length} of {total} results
          </div>
        )}
        
        {results.map((entry: any) => (
          <Card key={entry.id} className="p-4" data-testid={`result-${entry.id}`}>
            <h3 className="text-lg font-semibold">
              {highlightText(entry.title, searchQuery)}
            </h3>
            <div className="flex items-center gap-2 mt-2 mb-3">
              <span className="text-sm text-muted-foreground">
                {new Date(entry.createdAt).toLocaleDateString()}
              </span>
              {entry.mood && <Badge variant="outline">{entry.mood}</Badge>}
              {entry.matchCount > 0 && (
                <Badge variant="secondary">{entry.matchCount} matches</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {highlightText(entry.snippet, searchQuery)}
            </p>
            {entry.tags && entry.tags.length > 0 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {entry.tags.map((tag: string) => (
                  <Badge key={tag} variant="outline">
                    {highlightText(tag, searchQuery)}
                  </Badge>
                ))}
              </div>
            )}
          </Card>
        ))}

        {hasNextPage && !isLoading && (
          <div className="flex justify-center pt-4">
            <Button 
              onClick={() => fetchNextPage()}
              disabled={!hasNextPage || isFetchingNextPage}
              data-testid="button-load-more"
              variant="outline"
            >
              {isFetchingNextPage ? "Loading..." : `Load More (${results.length} of ${total})`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
